import type { ColorOption } from '@/mocks/finishingTree';

// ─── Process Type ───
export type ProcessType =
  | 'parts_replace'
  | 'finishing_color'
  | 'color_only'
  | 'finishing_only'
  | 'finishing_color_completed'
  | 'printing_add';

// ─── Customization Target ───
export interface CustomizationTarget {
  id: string;
  name: string;
  sku: string;
  imageUrl: string;
}

// ─── Tree Menu ───
export interface TreeMenuItem {
  id: string;
  label: string;
  labelKey?: string;
  icon: string;
  processType?: ProcessType;
  children?: TreeMenuItem[];
}

// ─── Replacement Parts ───
export interface ReplacementPart {
  id: string;
  name: string;
  type: 'cap' | 'pump';
  material: string;
  description: string;
  refColor?: string;
  imageUrl?: string;
  specs?: { label: string; value: string }[];
  displayPriceText?: string;
}

// ─── Printing Options ───
export interface PrintingOption {
  id: string;
  name: string;
  description: string;
  icon: string;
}

// ─── Added Process (page-level state) ───
export interface AddedProcess {
  id: string;
  processType: ProcessType;
  title: string;
  labelKey?: string;
}

// ─── Labels & Icons ───
export const PROCESS_TYPE_LABELS: Record<ProcessType, string> = {
  parts_replace: '부속품 교체 (CAP & PUMP)',
  finishing_color: '후가공 및 색상변경',
  color_only: '색상만 변경',
  finishing_only: '후가공만 변경',
  finishing_color_completed: '후가공 및 색상변경',
  printing_add: '후가공인쇄 추가',
};

export const PROCESS_TYPE_ICONS: Record<ProcessType, string> = {
  parts_replace: 'ri-exchange-line',
  finishing_color: 'ri-palette-line',
  color_only: 'ri-contrast-drop-line',
  finishing_only: 'ri-brush-line',
  finishing_color_completed: 'ri-palette-line',
  printing_add: 'ri-printer-line',
};

// ─── Mock Targets ───
export const mockTargets: CustomizationTarget[] = [
  {
    id: 'target-1',
    name: '에어리스 펌프 용기 50ml',
    sku: 'AP-50-WH',
    imageUrl: '',
  },
  {
    id: 'target-2',
    name: '로션 펌프 용기 30ml',
    sku: 'LP-30-CL',
    imageUrl: '',
  },
  {
    id: 'target-3',
    name: '스프레이 용기 100ml',
    sku: 'SP-100-GN',
    imageUrl: '',
  },
];

// ─── Tree Menu ───
export const customizationTree: TreeMenuItem[] = [
  {
    id: 'act-parts-replace',
    label: '부속품 교체 (CAP & PUMP)',
    labelKey: 'customize.tree.partsReplace',
    icon: 'ri-exchange-line',
    processType: 'parts_replace',
  },
  {
    id: 'act-finishing-color',
    label: '후가공 및 색상변경',
    labelKey: 'customize.tree.finishingColor',
    icon: 'ri-palette-line',
    processType: 'finishing_color',
  },
  {
    id: 'act-printing-add',
    label: '후가공 인쇄 추가',
    labelKey: 'customize.tree.printingAdd',
    icon: 'ri-printer-line',
    processType: 'printing_add',
  },
];

// ─── Printing Methods ───
export const printingOptions: PrintingOption[] = [
  {
    id: 'print-silkscreen',
    name: '실크스크린 인쇄',
    description: '단색/다색 인쇄, 평면 및 곡면 가능, 내구성 우수',
    icon: 'ri-brush-4-line',
  },
  {
    id: 'print-hotstamp',
    name: '핫스탬핑 (Hot Stamping)',
    description: '금박/은박 인쇄, 고급스러운 메탈릭 질감 연출',
    icon: 'ri-sparkling-2-line',
  },
  {
    id: 'print-pad',
    name: '패드 인쇄',
    description: '소형 부위 정밀 인쇄, 곡면 및 요철 부위에 적합',
    icon: 'ri-paint-brush-line',
  },
  {
    id: 'print-uv',
    name: 'UV 디지털 인쇄',
    description: '고해상도 풀컬러 인쇄, 빠른 건조 및 뛰어난 발색',
    icon: 'ri-sun-line',
  },
  {
    id: 'print-label',
    name: '라벨 / 스티커',
    description: '투명/유광/무광 라벨 부착, 디자인 자유도 높음',
    icon: 'ri-sticky-note-line',
  },
];

// ─── Printing Positions ───
export const printingPositions = [
  { id: '01', label: 'Body 전면', labelKey: 'customize.printPosition.bodyFront', icon: 'ri-layout-row-line' },
  { id: '02', label: '중간테두리', labelKey: 'customize.printPosition.middleRing', icon: 'ri-circle-line' },
  { id: '03', label: 'CAP 측면', labelKey: 'customize.printPosition.capSide', icon: 'ri-layout-column-line' },
  { id: '04', label: 'CAP 상단면', labelKey: 'customize.printPosition.capTop', icon: 'ri-layout-top-line' },
];